import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_ENDPOINTS } from '../../config/api';
import { authUtils } from '../../utils/auth';
import { validateVerificationCode } from '../../utils/validation';

const ResetPasswordCode: React.FC = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const email = authUtils.getEmail() || searchParams.get('email');

  useEffect(() => {
    if (!email) {
      setError('Email address not found. Please start the password reset process again.');
    }
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      navigate('/forgot-password');
      return;
    }

    if (!validateVerificationCode(code)) {
      setError('Please enter the 6-character verification code');
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.VERIFY_RESET_CODE, {
        email,
        code,
        recaptchaToken,
      });

      if (response.status === 200 && (response.data.change_password_key || response.data.password_reset_key)) {
        // Backend returns 'change_password_key' but we'll check both for compatibility
        const resetKey = response.data.change_password_key || response.data.password_reset_key;
        authUtils.setResetKey(resetKey);
        navigate('/new-password');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (value.length <= 6) {
      setCode(value);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-jobesta shadow-jobesta-card">
          <div className="rounded-jobesta bg-jobesta-red bg-opacity-10 p-4 border border-jobesta-red">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-jobesta-red">
                  Email address not found. Please start the password reset process again.
                </h3>
              </div>
            </div>
          </div>
          <div className="text-center">
            <a href="/forgot-password" className="text-sm text-jobesta-blue hover:text-jobesta-darkblue">
              Go to password reset
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-jobesta shadow-jobesta-card">
        <div>
          <h1 className="text-center text-3xl mb-2 tracking-tight">
            <span className="font-black text-jobesta-blue">Job</span>
            <span className="font-extralight text-jobesta-blue">esta</span>
          </h1>
          <h2 className="text-center text-xl font-semibold text-jobesta-gray-90">
            Enter verification code
          </h2>
          <p className="mt-2 text-center text-sm text-jobesta-gray-60">
            We've sent a verification code to {email}
          </p>
        </div>
        <form className="mt-8 space-y-6 ph-no-capture" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
              Verification Code
            </label>
            <input
              id="code"
              name="code"
              type="text"
              required
              className="appearance-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
              placeholder="XXXXXX"
              value={code}
              onChange={handleCodeChange}
              maxLength={6}
            />
          </div>

          {error && (
            <div className="rounded-jobesta bg-jobesta-red bg-opacity-10 p-4 border border-jobesta-red">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-jobesta-red">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            size="invisible"
          />

          <div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-jobesta text-white bg-jobesta-blue hover:bg-jobesta-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordCode;